import React from 'react';
import { connect } from 'react-redux';
import { Howl, HowlErrorCallback } from 'howler';
import * as T from './SoundPlayer.types';
import * as C from 'modules/Player/constants';
import * as SL from 'modules/Player/selectors';
import * as A from 'modules/Player/actions';
import { IRootState } from 'reducer.types';

export class SoundPlayer extends React.PureComponent<T.ISoundPlayerProps> {
  sound: null | Howl = null;
  initialized = false;
  durationIntervalId?: number;
  eventEndDispatched = false;
  eventPauseDispatched = false;
  eventSeekStart: null | number = null;

  constructor(props: T.ISoundPlayerProps) {
    super(props);

    this.initSoundPlayer();
  }

  componentDidUpdate(prevProps: T.ISoundPlayerProps): void {
    if (!this.sound) {
      this.initSoundPlayer();
    }

    if (prevProps.playStatus !== this.props.playStatus) {
      this.handlePlayStateChange();
    }

    if (prevProps.isSeeking !== this.props.isSeeking) {
      this.handleIsSeeking();
    }
  }

  componentWillUnmount(): void {
    const {
      dispatchTrackProgress,
      dispatchTrackDuration,
      dispatchSendLogEvent,
      currentlyPlayingTrackId,
    } = this.props;

    // This ensures, that when prevTrack ends/errored/was skipped,
    // track state values are reset to 0
    dispatchTrackProgress({
      durationPlayedInPermille: 0,
      durationPlayedInSeconds: 0,
    });
    dispatchTrackDuration({ trackDuration: 0 });

    // Should be before sound unloading
    if (this.sound && !this.eventEndDispatched && currentlyPlayingTrackId) {
      const seek = this.sound.seek();
      const trackTime = typeof seek === 'number' ? Math.floor(seek) : 0;

      // Same logic also in onEnd() func
      dispatchSendLogEvent({
        event: C.LOG_EVENT_SKIP,
        trackId: currentlyPlayingTrackId,
        trackTime,
      });

      this.eventEndDispatched = true;
    }

    if (this.sound) {
      this.sound.stop();
      this.sound.unload();
    }

    this.clearDurationInterval();
  }

  initSoundPlayer = () => {
    const { currentlyPlayingTrackUrl, soundRef } = this.props;

    if (!this.sound && currentlyPlayingTrackUrl && !this.initialized) {
      console.log('SoundPlayer.initSoundPlayer()', this.props);

      this.sound = new Howl({
        src: currentlyPlayingTrackUrl,
        html5: true,
        pool: 1,
        format: ['mp3', 'mp4', 'm4a'],
        onseek: this.onSeek,
        onload: this.onLoad,
        onend: this.onEnd,
        onplayerror: this.onPlayError,
        onloaderror: this.onLoadError,
      });

      soundRef.current = this.sound;
      this.initialized = true;

      this.handlePlayStateChange();
    }
  };

  handlePlayStateChange = ({ triggerPlay = true } = {}) => {
    const { dispatchSendLogEvent, currentlyPlayingTrackId } = this.props;

    if (this.sound && this.initialized) {
      const isPlaying = this.sound.playing();
      const { playStatus } = this.props;

      if (isPlaying && playStatus === C.STOPED) {
        this.clearDurationInterval();
        this.sound.stop();
      } else if (isPlaying && playStatus === C.PAUSED) {
        this.clearDurationInterval();
        this.sound.pause();

        if (this.sound && currentlyPlayingTrackId) {
          const seek = this.sound.seek();
          const trackTime = typeof seek === 'number' ? Math.floor(seek) : 0;

          dispatchSendLogEvent({
            event: C.LOG_EVENT_PAUSE,
            trackId: currentlyPlayingTrackId,
            trackTime,
          });

          this.eventPauseDispatched = true;
        }
      } else if (!isPlaying && playStatus === C.PLAYING && triggerPlay) {
        if (
          this.sound &&
          this.eventPauseDispatched &&
          currentlyPlayingTrackId &&
          typeof this.eventSeekStart !== 'number'
        ) {
          const seek = this.sound.seek();
          const trackTime = typeof seek === 'number' ? Math.floor(seek) : seek;

          if (typeof trackTime === 'number') {
            dispatchSendLogEvent({
              event: C.LOG_EVENT_PLAY_FROM_PAUSE,
              trackId: currentlyPlayingTrackId,
              trackTime,
            });
          } else {
            console.warn(
              `SoundPlayer.onPlay() - trackTime is not number -> skiping sending logEvent ${C.LOG_EVENT_PLAY_FROM_PAUSE} for track ${currentlyPlayingTrackId}`,
            );
          }
        }

        // This has to be after sending event
        this.clearDurationInterval();
        this.sound.play();
        this.initDurationInterval();
      }
    }
  };

  handleIsSeeking = () => {
    const { isSeeking } = this.props;

    if (isSeeking) {
      if (this.sound) {
        const seek = this.sound.seek();
        const eventSeekStart = typeof seek === 'number' ? Math.floor(seek) : seek;

        if (typeof eventSeekStart === 'number') {
          this.eventSeekStart = eventSeekStart;
        }
      }

      this.clearDurationInterval();
    } else {
      this.clearDurationInterval();
      this.initDurationInterval();
    }
  };

  clearDurationInterval = () => {
    clearInterval(this.durationIntervalId);
    this.durationIntervalId = undefined;
  };

  initDurationInterval = () => {
    this.durationIntervalId = window.setInterval(this.onDispatchTrackProgress, 500);
  };

  onDispatchTrackProgress = () => {
    const { dispatchTrackProgress } = this.props;

    if (this.sound && this.sound.playing()) {
      const seek = this.sound.seek();
      if (typeof seek === 'number') {
        const duration = this.sound.duration();

        dispatchTrackProgress({
          durationPlayedInPermille: seek / (duration / 1000),
          durationPlayedInSeconds: seek,
        });
      }
    }
  };

  onEnd = () => {
    const {
      currentlyPlayingTrackId,
      dispatchTrackEnd,
      nextAvailableTrackIdInPlaylist,
      dispatchSetPlayStatus,
      dispatchSendLogEvent,
    } = this.props;

    if (currentlyPlayingTrackId) {
      // Should be before dispatchTrackEnd
      // Same logic also in componentWillUnmount() for skip events
      if (this.sound && !this.eventEndDispatched) {
        const trackTime = this.sound.duration();

        dispatchSendLogEvent({
          event: C.LOG_EVENT_FINISHED,
          trackId: currentlyPlayingTrackId,
          trackTime,
        });

        this.eventEndDispatched = true;
      }

      dispatchTrackEnd({ trackId: currentlyPlayingTrackId });
    }

    if (!nextAvailableTrackIdInPlaylist) {
      dispatchSetPlayStatus({ playStatus: C.STOPED });
    }
  };

  onLoad = () => {
    const { dispatchTrackDuration } = this.props;

    if (this.sound) {
      dispatchTrackDuration({ trackDuration: this.sound.duration() });
    }
  };

  onSeek = () => {
    const { dispatchSendLogEvent, currentlyPlayingTrackId } = this.props;
    if (this.sound && currentlyPlayingTrackId) {
      const seek = this.sound.seek();
      const eventSeekEnd = typeof seek === 'number' ? Math.floor(seek) : seek;

      if (typeof eventSeekEnd === 'number' && typeof this.eventSeekStart === 'number') {
        dispatchSendLogEvent({
          event: C.LOG_EVENT_SEEK,
          trackId: currentlyPlayingTrackId,
          trackTime: this.eventSeekStart,
          trackFinalTime: eventSeekEnd,
        });
      } else {
        console.error(
          'SoundPlayer.onSeek() - eventSeekStart or eventSeekEnd are not available',
          this.eventSeekStart,
          eventSeekEnd,
        );
      }
    }

    this.eventSeekStart = null;
  };

  onLoadError: HowlErrorCallback = (_soundId: number, error: unknown) => {
    const { currentlyPlayingTrackId, currentlyPlayingTrackUrl } = this.props;

    console.error(
      'SoundPlayer.onLoadError()',
      currentlyPlayingTrackId,
      currentlyPlayingTrackUrl,
      error,
    );

    this.onSoundPlayerError();
  };

  onPlayError: HowlErrorCallback = (_soundId: number, error: unknown) => {
    const { currentlyPlayingTrackId, currentlyPlayingTrackUrl } = this.props;

    console.error(
      'SoundPlayer.onPlayError()',
      currentlyPlayingTrackId,
      currentlyPlayingTrackUrl,
      error,
    );

    this.onSoundPlayerError();
  };

  onSoundPlayerError = () => {
    const {
      nextAvailableTrackIdInPlaylist,
      dispatchSetCurrentlyPlayingTrackId,
      dispatchSetPlayStatus,
    } = this.props;

    if (nextAvailableTrackIdInPlaylist) {
      dispatchSetCurrentlyPlayingTrackId({
        id: nextAvailableTrackIdInPlaylist,
      });
    } else {
      dispatchSetPlayStatus({ playStatus: C.STOPED });
    }
  };

  render() {
    return null;
  }
}

const mapStateToProps = (state: IRootState) => ({
  currentlyPlayingTrackId: SL.getCurrentlyPlayingTrackId(state),
  currentlyPlayingTrackUrl: SL.getCurrentlyPlayingTrackUrl(state),
  isSeeking: SL.getIsSeeking(state),
  playStatus: SL.getPlayStatus(state),
  nextAvailableTrackIdInPlaylist: SL.getNextAvailableTrackIdInPlaylist(state),
});

const mapDispatchToProps = {
  dispatchTrackEnd: A.trackEnd,
  dispatchTrackProgress: A.trackProgress,
  dispatchTrackDuration: A.trackDuration,
  dispatchSetCurrentlyPlayingTrackId: A.setCurrentlyPlayingTrackId,
  dispatchSetPlayStatus: A.setPlayStatus,
  dispatchSendLogEvent: A.sendLogEvent,
};

export default connect(mapStateToProps, mapDispatchToProps)(SoundPlayer);
